import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { getActive } from '@theme/garage-store';

/**
 * Product recommendations, garage-aware.
 *
 * The section renders a same-vehicle row server-side (the product's first chassis collection)
 * whenever it can; this component handles the two dynamic cases:
 *   - default load: the section rendered skeletons (vehicle pool too small to fill the row, or
 *     no vehicle data), so fetch the recommendations endpoint, which merges the vehicle pool
 *     with ML backfill.
 *   - garage swap: the shopper's active garage car fits this product but is not the collection
 *     the server picked, so re-render the section through that car's collection URL
 *     (/collections/<car>/products/<product>?section_id=...) and morph it in. Re-runs on
 *     `vehicle:change`, so switching cars in the garage updates the row live.
 *
 * The server marks each render with data-vehicle-active (the chassis handle whose pool is shown)
 * and data-vehicle-count (how many same-vehicle cards it holds); a swap render with fewer than
 * two cards is discarded in favor of the default merged render so the row never looks bare.
 */
class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached section renders, keyed by fetch URL
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * The vehicle handle of the last swap we requested, so a swap that cannot be satisfied
   * (garage collection too sparse) does not retry forever. Reset on `vehicle:change`.
   * @type {string | null}
   */
  #requestedVehicle = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    document.addEventListener('vehicle:change', this.#onVehicleChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    document.removeEventListener('vehicle:change', this.#onVehicleChange);
  }

  #onVehicleChange = () => {
    this.#requestedVehicle = null;
    this.#loadRecommendations();
  };

  /**
   * Load the product recommendations from whichever source the garage state calls for
   */
  #loadRecommendations() {
    const { productId, sectionId, recommendationsPerformed } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    const desired = this.#desiredVehicle();
    const current = this.dataset.vehicleActive || '';
    const primary = this.#vehicleHandles()[0] || '';

    if (desired !== current && desired !== this.#requestedVehicle) {
      this.#requestedVehicle = desired;
      if (desired && desired !== primary) {
        this.#swapToVehicle(desired);
      } else {
        this.#fetchDefault();
      }
      return;
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchDefault();
  }

  /**
   * The chassis collection handles this product fits, in server (fitment chip) order
   * @returns {string[]}
   */
  #vehicleHandles() {
    return (this.dataset.vehicleHandles || '').split(/\s+/).filter(Boolean);
  }

  /**
   * The chassis handle the row should be built from: the shopper's active garage car when it
   * fits this product, otherwise the product's first chassis collection (or '' with no fitment).
   * @returns {string}
   */
  #desiredVehicle() {
    const handles = this.#vehicleHandles();
    if (handles.length === 0) return '';

    const active = getActive();
    const garageHandle = active ? this.#handleFromUrl(active.url) : null;
    if (garageHandle && handles.includes(garageHandle)) return garageHandle;

    return handles[0] || '';
  }

  /** "/collections/subaru-wrx-vb" (or an absolute URL) -> "subaru-wrx-vb". */
  #handleFromUrl(url) {
    if (!url) return null;
    let path = String(url);
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch {
      path = path.split('?')[0].split('#')[0];
    }
    const seg = path.replace(/\/+$/, '').split('/').pop();
    return seg ? seg.toLowerCase() : null;
  }

  /**
   * Fetch the default render: the recommendations endpoint, which the section merges with the
   * product's own vehicle pool server-side.
   */
  async #fetchDefault() {
    const { productId, sectionId, intent } = this.dataset;
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    try {
      const result = await this.#fetchCachedSection(url);

      if (!result.success) {
        // The Theme Editor will place a section element element in the DOM whose section_id is not available
        // to the Section Renderer API. In this case, we can safely ignore the error.
        if (!Shopify.designMode) {
          this.#handleError(new Error(`Server returned ${result.status}`));
        }
        return;
      }

      if (result.data?.trim().length) {
        this.dataset.recommendationsPerformed = 'true';
        morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
      } else {
        this.#handleError(new Error('No recommendations available'));
      }
    } catch (e) {
      this.#handleError(e);
    }
  }

  /**
   * Re-render the section through a chassis collection URL so the row is built from that car's
   * parts. Falls back to the default render when the result is too sparse to stand on its own.
   * @param {string} handle
   */
  async #swapToVehicle(handle) {
    const { sectionId, productHandle } = this.dataset;

    if (!productHandle) {
      return this.#fetchDefault();
    }

    const url = `/collections/${handle}/products/${productHandle}?section_id=${encodeURIComponent(sectionId)}`;

    try {
      const result = await this.#fetchCachedSection(url);

      if (!result.success) {
        return this.#fetchDefault();
      }

      const count = parseInt(result.data.match(/data-vehicle-count="(\d+)"/)?.[1] ?? '0', 10);
      if (count < 2) {
        return this.#fetchDefault();
      }

      this.dataset.recommendationsPerformed = 'true';
      morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
    } catch (e) {
      this.#handleError(e);
    }
  }

  /**
   * Fetches a section render and caches the result for future use
   * @param {string} url
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedSection(url) {
    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    if (error?.name === 'AbortError') return;
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
